import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { get, httpDelete, patch, post, put } from 'api/client';
import config from 'config';
import store from 'store';
import RequestHeaders from 'app/constants/requestHeaders';
import { CognitoTokenManager } from 'app/modules/auth';
import {
  ActivateModuleResponse,
  AddonsResponse,
  AttachBankTransferRequest,
  AttachBankTransferResponse,
  AttachPaymentMethodRequest,
  AttachStripePaymentMethodRequest,
  BillingCycle,
  ChangePreviewParams,
  ChangePreviewResponse,
  DeletePaymentMethodResponse,
  InvoicePaymentInstructionsResponse,
  InvoicesResponse,
  ManageModuleResponse,
  ModuleKey,
  ModulesResponse,
  NextInvoiceResponse,
  PaymentMethodClientTokenResponse,
  PaymentMethodResponse,
  PaymentMethodsResponse,
  PlansResponse,
  RetryInvoiceChargeResponse,
  SetDefaultPaymentMethodResponse,
  SetupPrerequisitesResponse,
  SetupSubscriptionRequest,
  SetupSummaryParams,
  SetupSummaryResponse,
  SocialProofResponse,
  StripeSetupIntentResponse,
  SubscribeRequest,
  SubscribeResponse,
  SubscriptionResponse,
  TopUpAiCreditsResponse,
  TrialResponse,
  UnlockedFeaturesResponse,
  UpdateAddonsRequest,
  UpdateBillingEmailRequest,
  UpdateBillingEmailResponse,
  V1UpgradePreviewResponse,
  UsageResponse,
} from 'app/modules/settings/modules/billing/types';
import { EntitlementsResponse } from 'app/modules/settings/modules/billing/types/entitlements';
import { buildReturnUrl } from 'app/modules/settings/modules/billing/utils/buildReturnUrl';

/**
 * BillingApi — thin wrappers around the new-billing endpoints documented at
 * `docs/new-billing-api.md` in the API repo. Every endpoint lives under
 * `/desktopapp/billing/*`, auth is handled by the shared axios client's
 * `authJoiner` interceptor (sends `x-http-jwt-token`), and the
 * `responseChecker` interceptor unwraps the `data` envelope for us — except
 * on paginated endpoints (`/invoices`) where we use a `transformResponse`
 * hook to re-shape the backend's `{ data, paging }` envelope into the
 * `{ data, meta }` shape the shared interceptor preserves intact (it keeps
 * the envelope only when `pagination` or `meta` is present).
 *
 * Invoice PDFs are fetched via `downloadInvoicePdf()` which pulls the file
 * as an authenticated blob — `window.open` wouldn't attach the
 * `x-http-jwt-token` header so a top-level navigation to the `downloadUrl`
 * would be rejected with 401.
 */

const CONFIG = {
  baseURL: config.API_SERVICE_ENDPOINT,
};

/**
 * Axios `transformResponse` runs before the shared `responseChecker`
 * interceptor. We parse the JSON as usual and copy `paging` onto a `meta`
 * field so the shared interceptor's "preserve envelope" branch fires and
 * returns the full `{ data, paging, meta }` object instead of unwrapping to
 * the inner `data` array. Other fields are untouched.
 */
const preserveInvoicesEnvelope = [
  (raw: unknown): unknown => {
    if (typeof raw !== 'string' || raw.length === 0) return raw;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && 'paging' in parsed) {
        return { ...parsed, meta: parsed.paging };
      }
      return parsed;
    } catch {
      return raw;
    }
  },
];

export type GetInvoicesParams = {
  limit?: number;
  cursor?: string;
};

export type BillingAddress = {
  name: string;
  companyName: string | null;
  vatNo: string | null;
  businessId: string | null;
  address1: string;
  address2: string | null;
  city: string;
  zip: string;
  country: string;
  state: string | null;
};

export type GetBillingAddressResponse = { address: BillingAddress | null };

export type VatValidationStatus =
  | 'Valid'
  | 'FormatInvalid'
  | 'ServiceUnavailable';

export type UpdateBillingAddressResponse = {
  address: BillingAddress;
  vatValidation: VatValidationStatus | null;
};

export type VatNumberSuggestionParams = {
  country: string;
  businessId: string;
};

export type VatNumberSuggestionResponse = { suggestion: string | null };

/**
 * Fetch a binary resource as a `Blob` via a raw axios call (no shared
 * interceptors) because the shared `responseChecker` rejects non-JSON
 * content-types — a PDF/CSV response would be rejected before we ever see
 * the bytes. Because we bypass `authJoiner`, we replicate the headers it
 * sets here: the JWT (`x-http-jwt-token`), the Cognito bearer token
 * (`Authorization`), and the selected account (`X-Account-Id`).
 */
async function authedBlobGet(path: string): Promise<Blob> {
  const authToken = store.getState().app.authToken;
  const accountId = CognitoTokenManager.getSelectedAccountId();
  const response = await axios.get<Blob>(path, {
    baseURL: config.API_SERVICE_ENDPOINT,
    responseType: 'blob',
    headers: {
      [RequestHeaders.HttpJwtToken]: authToken,
      ...(authToken
        ? { [RequestHeaders.Authorization]: `Bearer ${authToken}` }
        : {}),
      ...(accountId ? { [RequestHeaders.AccountId]: String(accountId) } : {}),
    },
  });
  return response.data;
}

export const BillingApi = {
  getSubscription(): Promise<SubscriptionResponse> {
    return get<SubscriptionResponse>('billing/subscription', CONFIG);
  },

  /**
   * Reverse a pending end-of-period cancellation (re-enable auto-renew). The
   * endpoint is idempotent, so a fresh key guards against accidental
   * double-submits. No charge — the next scheduled renewal proceeds.
   */
  reactivateSubscription(): Promise<{ active_until: string | null }> {
    return post<{ active_until: string | null }>(
      'subscription/reactivate',
      null,
      {
        ...CONFIG,
        headers: { 'Idempotency-Key': uuidv4() },
      },
    );
  },

  getUsage(): Promise<UsageResponse> {
    return get<UsageResponse>('billing/usage', CONFIG);
  },

  getNextInvoice(): Promise<NextInvoiceResponse> {
    return get<NextInvoiceResponse>('billing/next-invoice', CONFIG);
  },

  getPaymentMethod(): Promise<PaymentMethodResponse> {
    return get<PaymentMethodResponse>('billing/payment-method', CONFIG);
  },

  /**
   * List every manageable payment method for the account (api plan
   * 2026-07-03-payment-method-management). Default first, then newest-first.
   * Braintree rows are excluded except a legacy default (`isLegacy: true`).
   */
  getPaymentMethods(): Promise<PaymentMethodsResponse> {
    return get<PaymentMethodsResponse>('billing/payment-methods', CONFIG);
  },

  /**
   * Returns the full envelope (`{ data, paging }`) because the caller
   * needs `paging.nextCursor` for pagination. The `transformResponse`
   * hook mirrors `paging` to `meta` so the shared `responseChecker`
   * preserves the envelope (it only does so when `pagination` or `meta`
   * is present on `response.data`).
   */
  getInvoices(params?: GetInvoicesParams): Promise<InvoicesResponse> {
    return get<InvoicesResponse>('billing/invoices', {
      ...CONFIG,
      params,
      transformResponse: preserveInvoicesEnvelope,
    });
  },

  getAddons(): Promise<AddonsResponse> {
    return get<AddonsResponse>('billing/addons', CONFIG);
  },

  getModules(): Promise<ModulesResponse> {
    return get<ModulesResponse>('billing/modules', CONFIG);
  },

  getPlans(): Promise<PlansResponse> {
    return get<PlansResponse>('billing/plans', CONFIG);
  },

  getTrial(): Promise<TrialResponse> {
    return get<TrialResponse>('billing/trial', CONFIG);
  },

  getTrialUnlockedFeatures(): Promise<UnlockedFeaturesResponse> {
    return get<UnlockedFeaturesResponse>(
      'billing/trial/unlocked-features',
      CONFIG,
    );
  },

  getTrialSocialProof(): Promise<SocialProofResponse> {
    return get<SocialProofResponse>('billing/trial/social-proof', CONFIG);
  },

  /**
   * Preview the math for a v1→v2 grandfathered upgrade. Returns
   * `chargeAmount`, the remaining-value breakdown, and a `currencyFallback`
   * flag when the original sub's currency has no v2 pricing yet. The user
   * has not committed to anything until they POST /billing/subscribe with
   * `acceptV1Upgrade: true`.
   *
   * 422 when the account isn't grandfathered or the plan is unknown.
   */
  getV1UpgradePreview(
    planId: string,
    cycle: BillingCycle,
  ): Promise<V1UpgradePreviewResponse> {
    return get<V1UpgradePreviewResponse>(
      'billing/subscription/v1-upgrade-preview',
      {
        ...CONFIG,
        params: { planId, cycle },
      },
    );
  },

  /**
   * Preview a pending plan/cycle change before the user commits (api PR #700).
   * The monthly → annual cycle switch is now an IMMEDIATE pro-rated change, so
   * the FE previews `chargedToday` / `creditApplied` / savings via this endpoint
   * before firing `subscribe`. `mode` discriminates what the change does
   * (`cycle_switch_now | upgrade_now | scheduled | downgrade_scheduled | no_op`);
   * money fields are decimal strings. No charge is committed by this read.
   */
  getChangePreview(
    params: ChangePreviewParams,
  ): Promise<ChangePreviewResponse> {
    return get<ChangePreviewResponse>('billing/subscription/change-preview', {
      ...CONFIG,
      params,
    });
  },

  // -------------------------------------------------------------------------
  // Write endpoints
  // -------------------------------------------------------------------------

  /**
   * Change (or start) the subscription's plan + cycle. Returns a discriminated
   * union by `outcome` per BE Phase 8: `plan_change | upgrade_now | converted |
   * v1_upgrade | incomplete_created`. Idempotency-Key required by
   * `EnforceIdempotencyKey` middleware on the server (handoff §3.6). 422 on
   * invalid plan slug or unavailable plan; the FE surfaces the first
   * validation message.
   */
  subscribe(
    body: SubscribeRequest,
    idempotencyKey: string,
  ): Promise<SubscribeResponse> {
    return post<SubscribeResponse>(
      'billing/subscribe',
      { ...body, returnUrl: buildReturnUrl() },
      {
        ...CONFIG,
        headers: {
          ...(CONFIG as { headers?: Record<string, string> }).headers,
          'Idempotency-Key': idempotencyKey,
        },
      },
    );
  },

  getSetupPrerequisites(): Promise<SetupPrerequisitesResponse> {
    return get<SetupPrerequisitesResponse>(
      'billing/setup-prerequisites',
      CONFIG,
    );
  },

  getSetupSummary(params: SetupSummaryParams): Promise<SetupSummaryResponse> {
    return get<SetupSummaryResponse>('billing/setup-summary', {
      ...CONFIG,
      params,
    });
  },

  setupSubscription(
    body: SetupSubscriptionRequest,
    idempotencyKey: string,
  ): Promise<SubscriptionResponse> {
    return post<SubscriptionResponse>('billing/setup-subscription', body, {
      ...CONFIG,
      headers: {
        ...(CONFIG as { headers?: Record<string, string> }).headers,
        'Idempotency-Key': idempotencyKey,
      },
    });
  },

  /**
   * Persist new add-on quantities. Idempotency-Key required by
   * `EnforceIdempotencyKey` middleware on the server (handoff §3.6).
   */
  updateAddons(
    body: UpdateAddonsRequest,
    idempotencyKey: string,
  ): Promise<AddonsResponse> {
    return put<AddonsResponse>(
      'billing/addons',
      { ...body, returnUrl: buildReturnUrl() },
      {
        ...CONFIG,
        headers: {
          ...(CONFIG as { headers?: Record<string, string> }).headers,
          'Idempotency-Key': idempotencyKey,
        },
      },
    );
  },

  /**
   * Activate a module if it's available on the plan, or join its waitlist
   * if the module is still `coming`. The backend branches by module state;
   * the response's `module.state` tells the caller which happened.
   * Idempotency-Key required by the `idempotent` middleware on the server; a
   * fresh key guards the (possibly charging) activation against double-submits.
   */
  activateModule(key: ModuleKey): Promise<ActivateModuleResponse> {
    return post<ActivateModuleResponse>(
      `billing/modules/${key}/activate`,
      { returnUrl: buildReturnUrl() },
      {
        ...CONFIG,
        headers: { 'Idempotency-Key': uuidv4() },
      },
    );
  },

  /**
   * Schedule a module to stop at the end of the current period (universal —
   * every active module supports this). The module stays active + billed for
   * the current period; it is not renewed afterward. No proration / refund now.
   * Idempotent on the server. Returns the full module read so the card can show
   * "Cancels on <effectiveUntil>" without a re-fetch.
   */
  cancelModule(key: ModuleKey): Promise<ManageModuleResponse> {
    return post<ManageModuleResponse>(
      `billing/modules/${key}/cancel`,
      {},
      CONFIG,
    );
  },

  /**
   * Schedule a new GCM market count for the next renewal (GCM-specific, min 1).
   * The current count is billed for the current period; the new count applies
   * at renewal. No proration. Returns the full module read carrying
   * `scheduledMarkets`.
   */
  changeGcmMarkets(markets: number): Promise<ManageModuleResponse> {
    return patch<ManageModuleResponse>(
      'billing/modules/gcm/markets',
      { markets },
      CONFIG,
    );
  },

  /**
   * Fetch a fresh Braintree client token used to bootstrap the in-browser
   * Drop-in tokenizer. Tokens are short-lived and account-scoped — refetch
   * on every modal open rather than caching across the session.
   */
  getPaymentMethodClientToken(): Promise<PaymentMethodClientTokenResponse> {
    return get<PaymentMethodClientTokenResponse>(
      'billing/payment-method/client-token',
      CONFIG,
    );
  },

  /**
   * Attach a Braintree payment-method nonce tokenized on the client. Backend
   * maps gateway failures to 502; validation failures return 422.
   */
  attachPaymentMethod(
    body: AttachPaymentMethodRequest,
  ): Promise<PaymentMethodResponse> {
    return post<PaymentMethodResponse>('billing/payment-method', body, CONFIG);
  },

  /**
   * Step 1 of the Stripe Elements attach flow. Returns a `clientSecret` used
   * by `stripe.confirmSetup({ elements, clientSecret, ... })` after the user
   * fills the `<PaymentElement>` rendered by `StripePaymentMethodFields`. Idempotency-Key required by
   * `EnforceIdempotencyKey` middleware on the server.
   */
  createStripeSetupIntent(
    idempotencyKey: string,
  ): Promise<StripeSetupIntentResponse> {
    return post<StripeSetupIntentResponse>(
      'billing/stripe/setup-intent',
      {},
      {
        ...CONFIG,
        headers: {
          ...(CONFIG as { headers?: Record<string, string> }).headers,
          'Idempotency-Key': idempotencyKey,
        },
      },
    );
  },

  /**
   * Step 3 of the Stripe Elements attach flow (after `confirmSetup` runs
   * in the browser). Attaches the confirmed Stripe payment method to the
   * account server-side and marks it active. Returns the same shape as
   * `GET /billing/payment-method`.
   */
  attachStripePaymentMethod(
    body: AttachStripePaymentMethodRequest,
    idempotencyKey: string,
  ): Promise<PaymentMethodResponse> {
    return post<PaymentMethodResponse>('billing/payment-method/stripe', body, {
      ...CONFIG,
      headers: {
        ...(CONFIG as { headers?: Record<string, string> }).headers,
        'Idempotency-Key': idempotencyKey,
      },
    });
  },

  /**
   * Attach an offline bank-transfer (SEPA/wire) payment method, making it the
   * account default so the next subscribe/renewal settles offline (PENDING
   * invoice, subscription ACTIVE immediately). Backed by a Stripe
   * `customer_balance` method (EUR/USD only). Idempotency-Key required by the
   * `idempotent` middleware (handoff §3.8/§3.6); reuse the same key on retry
   * after a transient 502. Error mapping handled by the caller:
   *   422 → `errors.paymentMethod[0]` ("not enabled for bank transfer" /
   *         "not available for your billing currency")
   *   502 → transient gateway failure (`message`); safe to retry, same key.
   */
  attachBankTransferPaymentMethod(
    body: AttachBankTransferRequest,
    idempotencyKey: string,
  ): Promise<AttachBankTransferResponse> {
    return post<AttachBankTransferResponse>(
      'billing/payment-method/stripe-bank-transfer',
      body,
      {
        ...CONFIG,
        headers: {
          ...(CONFIG as { headers?: Record<string, string> }).headers,
          'Idempotency-Key': idempotencyKey,
        },
      },
    );
  },

  /**
   * Fetch a single invoice WITH resolved bankwire funding instructions. The
   * BE makes one live Stripe call to resolve the wire details, so this is
   * single-invoice only — the list endpoint never carries `paymentInstructions`.
   * `paymentInstructions` is `null` unless the invoice is an OFFLINE + PENDING
   * bankwire invoice with a funding intent. 404 on missing / cross-tenant
   * invoice (don't leak existence).
   */
  getInvoicePaymentInstructions(
    invoiceId: string,
  ): Promise<InvoicePaymentInstructionsResponse> {
    return get<InvoicePaymentInstructionsResponse>(
      `billing/invoices/${invoiceId}/payment-instructions`,
      CONFIG,
    );
  },

  /**
   * Change only the billing email (api #671). A plain idempotent PUT — no
   * Idempotency-Key middleware on the BE — that replaces the old workaround of
   * routing email-only edits through the payment-method attach endpoint.
   * The shared `responseChecker` unwraps the `{ data: { billingEmail } }`
   * envelope, so callers receive `{ billingEmail }`. Error mapping handled by
   * the caller via `errorMessage`: 422 → `errors.email[0]`; 502 → gateway down.
   */
  updateBillingEmail(email: string): Promise<UpdateBillingEmailResponse> {
    const body: UpdateBillingEmailRequest = { email };
    return put<UpdateBillingEmailResponse>(
      'billing/billing-email',
      body,
      CONFIG,
    );
  },

  getBillingAddress(): Promise<GetBillingAddressResponse> {
    return get<GetBillingAddressResponse>('billing/address', CONFIG);
  },

  updateBillingAddress(
    body: BillingAddress,
  ): Promise<UpdateBillingAddressResponse> {
    return put<UpdateBillingAddressResponse>('billing/address', body, CONFIG);
  },

  /**
   * Live VIES check for a single VAT number (api #869). The client-side format
   * guard (`isValidVatNumberFormat`) runs first; this confirms the number is
   * actually registered. `Valid` = VIES recognised it, `FormatInvalid` = VIES
   * could not verify it, `ServiceUnavailable` = VIES was unreachable (the FE
   * treats this as advisory and never blocks submit — fail open). The shared
   * `responseChecker` unwraps the `{ data: { status } }` envelope.
   */
  validateVatNumber(
    vatNumber: string,
  ): Promise<{ status: VatValidationStatus }> {
    return post<{ status: VatValidationStatus }>(
      'billing/vat/validate',
      { vat_number: vatNumber },
      CONFIG,
    );
  },

  /**
   * The VAT number a Business ID stands for when VIES confirms it, so a
   * customer who typed it into the wrong field still gets the reverse charge.
   */
  suggestVatNumber(
    params: VatNumberSuggestionParams,
  ): Promise<VatNumberSuggestionResponse> {
    return post<VatNumberSuggestionResponse>(
      'billing/vat/suggest',
      { country: params.country, business_id: params.businessId },
      CONFIG,
    );
  },

  /**
   * Detach the default payment method (backend returns 204; the shared
   * response wrapper resolves with `undefined`). 404 when no method is on
   * file — callers should check `usePaymentMethod` first.
   */
  detachPaymentMethod(): Promise<void> {
    return httpDelete<void>('billing/payment-method', CONFIG);
  },

  /**
   * Promote a saved method to default (demotes the previous default in one
   * BE transaction). 404 when not owned / soft-deleted; 422 for Braintree
   * or bank-transfer targets — the caller maps both (see spec §Error
   * handling).
   */
  setDefaultPaymentMethod(
    methodId: number,
  ): Promise<SetDefaultPaymentMethodResponse> {
    return post<SetDefaultPaymentMethodResponse>(
      `billing/payment-methods/${methodId}/default`,
      null,
      CONFIG,
    );
  },

  /**
   * Remove a saved method by id (soft-delete + Stripe detach server-side).
   * Returns `promoted` — the successor auto-promoted when the removed row
   * was the default (`null` otherwise) so the caller can name it in a
   * toast. 422 `last_payment_method` / Braintree / bank transfer; 404 not
   * owned. Supersedes the default-only `detachPaymentMethod()` above,
   * which is retired from FE use by this feature.
   */
  deletePaymentMethod(methodId: number): Promise<DeletePaymentMethodResponse> {
    return httpDelete<DeletePaymentMethodResponse>(
      `billing/payment-methods/${methodId}`,
      CONFIG,
    );
  },

  /**
   * Manual retry of a failed renewal invoice. Backend runs the dunning
   * service synchronously so we get an immediate result. Idempotency-Key
   * required by `EnforceIdempotencyKey` middleware on the server (handoff
   * §3.6). Error mapping:
   *   422 → `errors.payment` ("card_declined" or gateway-specific message)
   *   503 → `errors.payment` = "gateway_unavailable"
   *   409 → `errors.state`   = "no_retry_available" (state already shifted)
   *   404 → invoice missing or cross-account
   * The shared `responseChecker` interceptor unwraps the success envelope,
   * so callers receive `{ subscription, invoice }`.
   */
  retryInvoiceCharge(
    invoiceId: string,
    idempotencyKey: string,
  ): Promise<RetryInvoiceChargeResponse> {
    return post<RetryInvoiceChargeResponse>(
      `billing/invoices/${invoiceId}/retry-charge`,
      {},
      {
        ...CONFIG,
        headers: {
          ...(CONFIG as { headers?: Record<string, string> }).headers,
          'Idempotency-Key': idempotencyKey,
        },
      },
    );
  },

  /**
   * Fetch an invoice PDF as a `Blob` via `authedBlobGet` — the shared
   * `responseChecker` rejects non-JSON content-types, and a top-level
   * navigation to the `downloadUrl` wouldn't carry the auth headers.
   */
  downloadInvoicePdf(invoiceId: string): Promise<Blob> {
    return authedBlobGet(`billing/invoices/${invoiceId}/pdf`);
  },

  /**
   * Fetch every invoice as a single CSV `Blob` (api PR #668, returns a CSV
   * attachment) via `authedBlobGet` — same authed raw-axios pattern as
   * `downloadInvoicePdf`.
   */
  exportInvoicesCsv(): Promise<Blob> {
    return authedBlobGet('billing/invoices/export');
  },

  /**
   * Download the account's full invoice history as a single zip of per-invoice
   * PDFs. Uses the authed-blob path (the shared interceptor rejects non-JSON);
   * a 422 (empty history / over the bundle cap) arrives as a Blob error body —
   * the caller reads it for the user-facing message.
   */
  exportInvoicesPdfZip(): Promise<Blob> {
    return authedBlobGet('billing/invoices/export/pdf');
  },

  getEntitlements(): Promise<EntitlementsResponse> {
    return get<EntitlementsResponse>('billing/entitlements', CONFIG);
  },

  /**
   * Purchase AI-credit packs (immediate charge, credits granted on settlement).
   * `packs` is the number of packs to buy (1–`maxPacks` from the catalog).
   * Returns `creditsGranted`, `newBalance`, and the top-up invoice.
   * Idempotency-Key guards against accidental double-submits (see
   * `EnforceIdempotencyKey` middleware on the server).
   */
  topUpAiCredits(
    body: { packs: number },
    idempotencyKey: string,
  ): Promise<TopUpAiCreditsResponse> {
    return post<TopUpAiCreditsResponse>(
      'billing/ai-credits/top-up',
      { ...body, returnUrl: buildReturnUrl() },
      {
        ...CONFIG,
        headers: {
          ...(CONFIG as { headers?: Record<string, string> }).headers,
          'Idempotency-Key': idempotencyKey,
        },
      },
    );
  },
};
